import { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { workSessionService } from '../../services';
import { UPDATE_FAILURE_ERROR } from '../../constants';
import { DateAndTimePickerField } from '../common';

const ProjectEditWorkSessionModal = ({
  wsItem,
  projectsList,
  handleUpdateWsData,
}) => {
  const { user, project: selectedProject } = wsItem;
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [isSubmitting, setSubmitting] = useState(false);
  const [checkInDate, setCheckInDate] = useState(wsItem.checkInDate);
  const [checkOutDate, setCheckOutDate] = useState(wsItem.checkOutDate);
  const checkInDateISOformat = new Date(wsItem.checkInDate).toISOString();
  const checkOutDateISOformat = new Date(wsItem.checkOutDate).toISOString();
  const memberProjects = projectsList.filter(
    (item) =>
      item._id !== selectedProject._id &&
      item.members.some((member) => user._id === member._id)
  );
  const userDetails = useSelector((state) => state.userReducer);

  const handleUpdateWorkSession = (formdata) => {
    if (Date.parse(checkOutDate) <= Date.parse(checkInDate)) {
      toast.error('Check in time is greater or equal to check out time');
    } else {
      setSubmitting(true);
      const params = {
        teamOwnerId: localStorage.getItem('teamOwnerId'),
        workSessionId: wsItem._id,
        workSessionDetails: {
          checkInDate: moment(checkInDate).format('YYYY-MM-DD HH:mm:ss'),
          checkOutDate: moment(checkOutDate).format('YYYY-MM-DD HH:mm:ss'),
          reason: formdata.reason,
          projectId: formdata.project,
        },
      };

      workSessionService
        .update_worksession(params)
        .then((response) => {
          if (response.status) {
            handleUpdateWsData();
            setSubmitting(false);
            toast.success('Worksession has been updated successfully');
            handleClose();
          }
        })
        .catch((error) => {
          if (error.message === 'Validation error') {
            if (Array.isArray(error.data)) {
              error.data.map((item) => toast.error(item.msg));
            } else {
              toast.error(error.data);
            }
          } else if (error.message) {
            toast.error(error.message);
          } else {
            toast.error(UPDATE_FAILURE_ERROR);
          }
          setSubmitting(false);
        });
    }
  };

  return (
    <>
      <Button variant="light" size="sm" onClick={handleShow}>
        Edit Worksession
      </Button>

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header>
          <Modal.Title className="m-0">Edit worksession</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            checkInDate,
            checkOutDate,
            reason:
              wsItem.manualEntryReason !== null ? wsItem.manualEntryReason : '',
            project: selectedProject._id,
          }}
          validationSchema={Yup.object({
            checkInDate: Yup.date().required(
              'Please enter check in information'
            ),
            checkOutDate: Yup.date().required(
              'Please enter check out information'
            ),
            reason: Yup.string().required(
              'Please enter a reason for your worksession'
            ),
            notes: Yup.string().min(5, 'Note must be more than 5 characters'),
          })}
          onSubmit={(values) => {
            handleUpdateWorkSession(values);
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              <Modal.Body>
                <section>
                  <Form.Group controlId="memberName">
                    <Form.Label>Team Member</Form.Label>
                    <h3>{`${user.firstName} ${user.lastName}`}</h3>
                  </Form.Group>
                  <DateAndTimePickerField
                    label="Check In"
                    handleDateSelect={setCheckInDate}
                    name="checkInDate"
                    defaultValue={checkInDateISOformat}
                    defaultValueDisplay
                  />
                  <DateAndTimePickerField
                    label="Check Out"
                    handleDateSelect={setCheckOutDate}
                    name="checkOutDate"
                    defaultValue={checkOutDateISOformat}
                    defaultValueDisplay
                  />
                  <Form.Group controlId="reason">
                    <Form.Label>
                      Reason <span className="text-danger">*</span>
                    </Form.Label>
                    <Field
                      className="form-control"
                      type="text"
                      placeholder=""
                      name="reason"
                    />
                    <ErrorMessage
                      name="reason"
                      render={(msg) => (
                        <small className="text-danger">{msg}</small>
                      )}
                    />
                  </Form.Group>
                  <Form.Group controlId="Project">
                    <Form.Label>
                      Project <span className="text-danger">*</span>
                    </Form.Label>
                    <Field
                      className="form-select"
                      as="select"
                      name="project"
                      disabled={userDetails.role !== 'creator'}
                    >
                      <option value={selectedProject._id}>
                        {selectedProject.title}
                      </option>
                      {memberProjects.length > 0 &&
                        memberProjects.map((project, index) => (
                          <option key={index} value={project._id}>
                            {project.title}
                          </option>
                        ))}
                    </Field>
                  </Form.Group>
                </section>
              </Modal.Body>
              <Modal.Footer className="d-flex justify-content-between">
                <Button variant="link" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting && (
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                  )}
                  Save
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

ProjectEditWorkSessionModal.propTypes = {
  wsItem: PropTypes.object,
  projectsList: PropTypes.array,
  handleUpdateWsData: PropTypes.func,
};

export default ProjectEditWorkSessionModal;
